import React, { useState, Component } from 'react';
import { XIcon, ClockIcon, PhoneIcon, AlertCircleIcon, SmileIcon, CopyIcon, MessageSquareIcon, CalendarIcon, PackageIcon, TargetIcon, ActivityIcon } from 'lucide-react';
interface Lead {
  id: number;
  name: string;
  initials?: string;
  email: string;
  phone: string;
  callDuration?: string;
  leadType: 'cold' | 'warm' | 'hot';
  status: string;
  dealerCode?: string;
  dealerName?: string;
  carModel?: string;
}
interface LeadDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
}
// AI Icon Component
function AIIcon({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center ${className}`} data-id="element-423">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600" data-id="element-424">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" data-id="element-425" />
      </svg>
    </div>;
}
// User Avatar Component
function UserAvatar({
  initials,
  className = ''
}: {
  initials: string;
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-white text-sm font-bold ${className}`} data-id="element-426">
      {initials}
    </div>;
}
export function LeadDetailOverlay({
  isOpen,
  onClose,
  lead
}: LeadDetailOverlayProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'ai-analytics'>('details');
  const [leadStatus, setLeadStatus] = useState('buy-mahindra-car');
  const [buyStatus, setBuyStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  if (!isOpen || !lead) return null;
  const userInitials = lead.initials || lead.name.substring(0, 2).toUpperCase();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300" onClick={onClose} data-id="element-427" />

      {/* Overlay Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col" data-id="element-428">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-429">
          <div className="flex items-center gap-2" data-id="element-430">
            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center" data-id="element-431">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" data-id="element-432">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-433" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-434">
              Lead Details
            </h2>
          </div>
          <div className="flex items-center gap-3" data-id="element-435">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-436">
              <ClockIcon size={16} data-id="element-437" />
              ACTIVITY LOGS
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors" data-id="element-438">
              <XIcon size={20} data-id="element-439" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6" data-id="element-440">
          <button onClick={() => setActiveTab('details')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-441">
            DETAILS
          </button>
          <button onClick={() => setActiveTab('ai-analytics')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'ai-analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-442">
            AI ANALYTICS
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-443">
          {activeTab === 'details' && <div className="space-y-5" data-id="element-444">
              {/* Status Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700" data-id="element-445">
                <PhoneIcon size={16} data-id="element-446" />
                <span className="font-medium" data-id="element-447">
                  Call routed to dealer and answered.
                </span>
              </div>

              {/* Name and Email */}
              <div className="grid grid-cols-2 gap-4" data-id="element-448">
                <div data-id="element-449">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-450">
                    Name
                  </label>
                  <input type="text" value={lead.name} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-451" />
                </div>
                <div data-id="element-452">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-453">
                    Email
                  </label>
                  <input type="email" value={lead.email} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-454" />
                </div>
              </div>

              {/* Phone Number, Call Duration, Lead Type */}
              <div className="grid grid-cols-3 gap-4" data-id="element-455">
                <div data-id="element-456">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-457">
                    Phone Number
                  </label>
                  <input type="tel" value={lead.phone} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-458" />
                </div>
                <div data-id="element-459">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-460">
                    Call Duration
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-461">
                    <ClockIcon size={14} className="text-blue-600" data-id="element-462" />
                    <span className="text-sm font-semibold text-blue-600" data-id="element-463">
                      {lead.callDuration || '12:21 Sec'}
                    </span>
                  </div>
                </div>
                <div data-id="element-464">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-465">
                    Lead Type
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-466">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-467">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-468">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-469" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-600" data-id="element-470">
                      Cold
                    </span>
                  </div>
                </div>
              </div>

              {/* Lead Status Radio Buttons */}
              <div data-id="element-471">
                <label className="block text-xs font-medium text-gray-700 mb-3" data-id="element-472">
                  Lead Status
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-473">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-474">
                    <input type="radio" name="leadStatus" value="buy-mahindra-car" checked={leadStatus === 'buy-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-475" />
                    <span className="text-sm text-gray-700 font-medium" data-id="element-476">
                      BUY A MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-477">
                    <input type="radio" name="leadStatus" value="service-mahindra-car" checked={leadStatus === 'service-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-478" />
                    <span className="text-sm text-gray-500" data-id="element-479">
                      SERVICE MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-480">
                    <input type="radio" name="leadStatus" value="feedback" checked={leadStatus === 'feedback'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-481" />
                    <span className="text-sm text-gray-500" data-id="element-482">FEEDBACK</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-483">
                    <input type="radio" name="leadStatus" value="non-sale" checked={leadStatus === 'non-sale'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-484" />
                    <span className="text-sm text-gray-500" data-id="element-485">NON SALE</span>
                  </label>
                </div>
              </div>

              {/* Buy A Mahindra Car Dropdown */}
              <div data-id="element-486">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-487">
                  Buy A Mahindra Car
                </label>
                <select value={buyStatus} onChange={e => setBuyStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.5rem_center] bg-no-repeat pr-10" data-id="element-488">
                  <option value="" data-id="element-489">Select Status</option>
                  <option value="interested" data-id="element-490">Interested</option>
                  <option value="test-drive" data-id="element-491">Test Drive Scheduled</option>
                  <option value="negotiating" data-id="element-492">Negotiating</option>
                  <option value="closed" data-id="element-493">Closed</option>
                </select>
              </div>

              {/* Notes */}
              <div data-id="element-494">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-495">
                  Notes
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Enter Details" rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-496" />
              </div>

              {/* Sync State Progress */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-497">
                <label className="block text-sm font-semibold text-gray-700 mb-4" data-id="element-498">
                  Sync State
                </label>
                <div className="space-y-3" data-id="element-499">
                  <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-500">
                    <span className="text-gray-600" data-id="element-501">CREATED</span>
                    <span className="text-gray-600" data-id="element-502">OPENED</span>
                    <span className="text-gray-600" data-id="element-503">SAVED</span>
                    <span className="text-blue-600" data-id="element-504">SYNCED</span>
                    <span className="text-gray-400" data-id="element-505">ENQUIRY</span>
                  </div>
                  <div className="flex items-center" data-id="element-506">
                    <div className="flex-1 relative" data-id="element-507">
                      <div className="h-3 bg-blue-600 rounded-l-full" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)'
                  }} data-id="element-508"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-509">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-510"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-511">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-512"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-513">
                      <div className="h-3 bg-blue-800" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-514"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-515">
                      <div className="h-3 bg-gray-300 rounded-r-full" style={{
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 8px 50%)'
                  }} data-id="element-516"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-517">
                    <AlertCircleIcon size={14} data-id="element-518" />
                    <span data-id="element-519">Syncing Failed on 15 Nov 2024</span>
                  </div>
                </div>
              </div>

              {/* Model Dropdown */}
              <div data-id="element-520">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-521">
                  Model
                </label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="w-full px-3 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.75rem_center] bg-no-repeat pr-10" data-id="element-522">
                  <option value="" data-id="element-523">Select model</option>
                  <option value="thar" data-id="element-524">Thar</option>
                  <option value="thar-roxx" data-id="element-525">Thar Roxx</option>
                  <option value="scorpio-n" data-id="element-526">Scorpio-N</option>
                  <option value="xuv700" data-id="element-527">XUV700</option>
                  <option value="xuv400" data-id="element-528">XUV400</option>
                  <option value="bolero" data-id="element-529">Bolero</option>
                  <option value="bolero-neo" data-id="element-530">Bolero Neo</option>
                </select>
              </div>

              {/* Lead Created On */}
              <div data-id="element-531">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-532">
                  Lead Created on
                </label>
                <input type="text" value="15 Nov 2024" readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-533" />
              </div>

              {/* Dealer Code and Dealer Name */}
              <div className="grid grid-cols-2 gap-4" data-id="element-534">
                <div data-id="element-535">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-536">
                    Dealer Code
                  </label>
                  <input type="text" value={lead.dealerCode || 'HR407'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-537" />
                </div>
                <div data-id="element-538">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-539">
                    Dealer Name
                  </label>
                  <input type="text" value={lead.dealerName || 'Prem Kumar'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-540" />
                </div>
              </div>
            </div>}

          {activeTab === 'ai-analytics' && <div className="space-y-6" data-id="element-541">
              {/* Call Back Reason Banner */}
              <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg px-4 py-3" data-id="element-542">
                <div className="flex items-center gap-2 text-orange-600 font-semibold text-sm mb-1" data-id="element-543">
                  <PhoneIcon size={16} data-id="element-544" />
                  CALL BACK REASON
                </div>
                <p className="text-gray-700 text-sm" data-id="element-545">
                  Voicebot agent didn't understand user inputs
                </p>
              </div>

              {/* Call Data Section */}
              <div className="bg-gray-50 rounded-xl p-5" data-id="element-546">
                <div className="flex items-center gap-2 mb-4" data-id="element-547">
                  <ActivityIcon size={18} className="text-blue-600" data-id="element-548" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-549">
                    CALL DATA
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-5" data-id="element-550">
                  <div className="flex items-center gap-3" data-id="element-551">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-552">
                      <ClockIcon size={20} className="text-blue-600" data-id="element-553" />
                    </div>
                    <div data-id="element-554">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-555">Duration</p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-556">04:32</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-557">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-558">
                      <SmileIcon size={20} className="text-blue-600" data-id="element-559" />
                    </div>
                    <div data-id="element-560">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-561">Sentiment</p>
                      <p className="text-lg font-bold text-green-500" data-id="element-562">
                        POSITIVE
                      </p>
                    </div>
                  </div>
                </div>

                <div data-id="element-563">
                  <div className="flex items-center justify-between mb-2" data-id="element-564">
                    <span className="text-sm text-gray-600" data-id="element-565">
                      Call Completion Status
                    </span>
                    <span className="text-sm font-bold text-green-500" data-id="element-566">
                      100%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-567">
                    <div className="h-full bg-green-500 rounded-full" style={{
                  width: '100%'
                }} data-id="element-568"></div>
                  </div>
                </div>
              </div>

              {/* Call Intent Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-569">
                <div className="flex items-center gap-2 mb-4" data-id="element-570">
                  <TargetIcon size={18} className="text-blue-600" data-id="element-571" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-572">
                    CALL INTENT
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6" data-id="element-573">
                  <div data-id="element-574">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-575">Reason</p>
                    <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full text-sm font-medium" data-id="element-576">
                      Purchase Intent
                    </span>
                  </div>
                  <div data-id="element-577">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-578">Purpose</p>
                    <p className="text-sm text-gray-700" data-id="element-579">
                      Inquiring about new SUV models and test drive
                      availability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Interest Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-580">
                <div className="flex items-center gap-2 mb-4" data-id="element-581">
                  <PackageIcon size={18} className="text-blue-600" data-id="element-582" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-583">
                    PRODUCT INTEREST
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-4" data-id="element-584">
                  <div data-id="element-585">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-586">Product</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-587">
                      Tata Harrier XZA+
                    </p>
                  </div>
                  <div data-id="element-588">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-589">Budget</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-590">
                      ₹20L - ₹25L
                    </p>
                  </div>
                </div>

                <div data-id="element-591">
                  <p className="text-xs text-blue-500 mb-2" data-id="element-592">Preferences</p>
                  <div className="flex flex-wrap gap-2" data-id="element-593">
                    {['Dark Edition', 'Automatic', 'Sunroof', 'Diesel'].map(pref => <span key={pref} className="px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm text-gray-700" data-id="element-594">
                          {pref}
                        </span>)}
                  </div>
                </div>
              </div>

              {/* Timeline / Preferences Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-595">
                <div className="flex items-center gap-2 mb-4" data-id="element-596">
                  <CalendarIcon size={18} className="text-blue-600" data-id="element-597" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-598">
                    TIMELINE / PREFERENCES
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-599">
                  <div data-id="element-600">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-601">
                      Expected Purchase
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-602">
                      Within 2 weeks
                    </p>
                  </div>
                  <div data-id="element-603">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-604">
                      Conversion Score
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-605">High</p>
                  </div>
                  <div data-id="element-606">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-607">Contact Time</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-608">
                      After 6:00 PM
                    </p>
                  </div>
                  <div data-id="element-609">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-610">Appointment</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-611">
                      Scheduled: Sat, 06th Dec, 11:00 AM
                    </p>
                  </div>
                  <div data-id="element-612">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-613">Channel</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-614">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Question / Conversation by Voicebot Agent */}
              <div data-id="element-615">
                <div className="flex items-center gap-2 mb-4" data-id="element-616">
                  <MessageSquareIcon size={18} className="text-blue-600" data-id="element-617" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-618">
                    QUESTION / CONVERSATION BY VOICEBOT AGENT
                  </h3>
                </div>

                <div className="space-y-4" data-id="element-619">
                  {/* Name Question */}
                  <div className="flex items-start gap-3" data-id="element-620">
                    <AIIcon data-id="element-621" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-622">Name?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-623">
                    <UserAvatar initials={userInitials} data-id="element-624" />
                    <div className="pt-2" data-id="element-625">
                      <p className="text-sm text-gray-900" data-id="element-626">{lead.name}.</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-627">
                        <CopyIcon size={14} data-id="element-628" />
                      </button>
                    </div>
                  </div>

                  {/* Email Question */}
                  <div className="flex items-start gap-3" data-id="element-629">
                    <AIIcon data-id="element-630" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-631">Email?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-632">
                    <UserAvatar initials={userInitials} data-id="element-633" />
                    <div className="pt-2" data-id="element-634">
                      <p className="text-sm text-gray-900" data-id="element-635">{lead.email}</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-636">
                        <CopyIcon size={14} data-id="element-637" />
                      </button>
                    </div>
                  </div>

                  {/* Model Interested Question */}
                  <div className="flex items-start gap-3" data-id="element-638">
                    <AIIcon data-id="element-639" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-640">
                      Model Interested?
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-641">
                    <UserAvatar initials={userInitials} data-id="element-642" />
                    <div className="pt-2" data-id="element-643">
                      <p className="text-sm text-gray-900" data-id="element-644">
                        {lead.carModel || 'Scorpio-N'}
                      </p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-645">
                        <CopyIcon size={14} data-id="element-646" />
                      </button>
                    </div>
                  </div>

                  {/* Test Drive Question */}
                  <div className="flex items-start gap-3" data-id="element-647">
                    <AIIcon data-id="element-648" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-649">Test Drive?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-650">
                    <UserAvatar initials={userInitials} data-id="element-651" />
                    <div className="pt-2" data-id="element-652">
                      <p className="text-sm text-gray-500 italic" data-id="element-653">
                        Not Answered...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white" data-id="element-654">
          <button className="px-6 py-2.5 text-sm font-semibold text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors" data-id="element-655">
            SAVE LEAD
          </button>
          <button className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm" data-id="element-656">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-657">
              <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-658" />
            </svg>
            SYNC & SAVE
          </button>
        </div>
      </div>
    </>;
}